import { put, takeLatest, call } from 'redux-saga/effects';
import { SIGNUP_REQUEST, LOGIN_REQUEST, LOGOUT_REQUEST, REHYDRATE_AUTH, LOGIN } from './constants';
import { ISignUpRequestAction, ILoginRequestAction, ILoginAction } from './types';
import { login, logout, rehydrateComplete } from './actions';
import { authStorage } from '../../services/authStorage';

const SECURITY_API_BASE_URL = `${process.env.REACT_APP_SECURITY_API_BASE_URL}`

function* signUpRequest(action: ISignUpRequestAction): Generator<any, void, any> {
  try {
    const axios = require('axios').default;
    const url = `${SECURITY_API_BASE_URL}/user/signup`;

    const postData = {
      name: action.name,
      email: action.email,
      password: action.password
    };

    // Use call() effect for proper saga typing and flow control
    let response: any;
    try {
      response = yield call([axios, 'post'], url, postData);
    } catch (axiosError: any) {
      // Pass the error to callback and exit
      if (action.callback) {
        action.callback(undefined, axiosError);
      }
      return;
    }

    if (response?.data) {
      const authData = {
        type: LOGIN,
        userId: response.data.userId,
        email: response.data.email,
        token: response.data.token,
        expirationDate: response.data.expirationDate || (Date.now() + 3600000),
        roles: response.data.roles || [{ role: 'USER' }]
      };

      // Update Redux state FIRST
      yield put(login(authData));
      authStorage.save(authData);

      console.log('✅ User signed up and authenticated:', authData.email, 'userId:', authData.userId);

      // THEN call callback after state is updated
      if (action.callback) {
        action.callback(response, undefined);
      }
    }

  } catch (e: any) {
    console.error('❌ SignUp saga error:', e);
    if (action.callback) {
      action.callback(undefined, e);
    }
  }
}

function* loginRequest(action: ILoginRequestAction, retry: number = 2) {
  try {
    const API_URL = SECURITY_API_BASE_URL;
    let url = `${API_URL}/user/login`;  // Backend endpoint: /user/login

    const axios = require('axios').default;
    let resp: any;

    var postData = {
      email: action.email,
      password: action.password
    };

    yield axios.post(url, postData)
      .then(function (response: any) {
        if (response) {
          resp = response;
          if (action.callback) {
            action.callback(response, undefined);
          }
        } else {
          if (action.callback) {
            action.callback(undefined, response);
          }
        }
      })
      .catch(function (error: any) {
        if (error) {
          if (action.callback) {
            action.callback(undefined, error);
          }
        }
      });

    if (resp) {
      yield put(login(resp.data));
      // Save auth user to storage after successful login
      authStorage.save(resp.data);
    }

  } catch (e) {
    if (e === 'Network Error' && retry > 0) {
    } else {
      const attributes = {
        type: 'SAGAS ERROR CATCHED',
        action,
        location: 'state/auth/sagas - loginRequest',
      };
      if (action.callback) {
        action.callback(undefined, attributes);
      }
    }
  }
}

function* logoutRequest(action: ILoginAction, retry: number = 2) {
  try {
    // Backend has no logout endpoint - Cognito handles auth via tokens
    // Just clear local auth state
    yield put(logout());
    authStorage.clear();

    console.log('✅ [AUTH] User logged out successfully (client-side)');
  } catch (e) {
    console.error('❌ [AUTH] Logout error:', e);
    // Still clear auth even if error occurs
    yield put(logout());
    authStorage.clear();
  }
}

function* rehydrateAuth() {
  try {
    // Load auth user from storage
    const authUser = authStorage.load();
    
    if (authUser) {
      // If valid auth user found, restore it to Redux state
      yield put(login(authUser));
    } else {
      // No user found, mark rehydration as complete
      yield put(rehydrateComplete());
    }
  } catch (e) {
    // If rehydration fails, just continue without auth
    console.error('Failed to rehydrate auth state:', e);
    yield put(rehydrateComplete());
  }
}

export default function* authSagas() {
  yield takeLatest(SIGNUP_REQUEST, signUpRequest);
  yield takeLatest(LOGIN_REQUEST, loginRequest);
  yield takeLatest(LOGOUT_REQUEST, logoutRequest);
  yield takeLatest(REHYDRATE_AUTH, rehydrateAuth);
}
